// SPEC_DEMO.md PR-5. Minimal fetch wrapper for the public demo route.
//
// Always sends `X-Demo-Mode: true`. Never consults Auth0. Same origin
// (relative `/api/v1/...`) so nginx routes to the backend without any
// CORS dance, and so the demo bundle can be served from
// https://seidr.site/demo even when the marketing iframe lives on
// https://www.seidrplatform.com.

import demoFixture from './demoFixture.json';

const API_BASE = import.meta.env?.VITE_API_URL || '';

const HEADERS = {
  'Content-Type': 'application/json',
  // Backend's get_demo_org_id_or_authenticated_org dep keys on this
  // header (case-insensitive comparison against the literal "true").
  'X-Demo-Mode': 'true',
};

async function _fetch(path, init = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { ...HEADERS, ...(init.headers || {}) },
  });
  if (!res.ok) {
    let detail = '';
    try { detail = JSON.stringify(await res.json()); } catch { /* not JSON */ }
    throw new Error(`${path} → ${res.status}${detail ? ' ' + detail : ''}`);
  }
  return res.json();
}

export function demoScreen(params) {
  return _fetch('/api/v1/demo/screen', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

export function demoSubstations() {
  return _fetch('/api/v1/demo/layers/substations');
}

export function demoGrid220kv() {
  return _fetch('/api/v1/demo/layers/grid');
}

export function demoPowerlines() {
  return _fetch('/api/v1/demo/layers/powerlines');
}

export function demoPorts() {
  return _fetch('/api/v1/demo/layers/ports');
}

// Replay a pre-captured run from demoFixture.json with compressed timing.
// The real engine takes ~20-30s; replaying in ~3.5s keeps the funnel
// animation visible without making the buyer wait. The `complete` event
// still carries the real engine's `search_duration_seconds` so the
// headline reads "Screened in 21.2 seconds" — honest about engine
// runtime, just doesn't block the demo on it.
//
// Refresh the fixture via `python3 scripts/capture_demo_fixture.py` when
// the engine output drifts.
const REPLAY_TARGET_MS = 3500;
const REPLAY_MIN_GAP_MS = 80;

export function demoReplayStream(onEvent, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const realDurationMs = Math.max(1, demoFixture[demoFixture.length - 1].t * 1000);
    const speedup = realDurationMs / REPLAY_TARGET_MS;

    const timers = [];
    function onAbort() {
      timers.forEach(clearTimeout);
      reject(new DOMException('Aborted', 'AbortError'));
    }
    signal?.addEventListener('abort', onAbort);

    let prevScheduled = 0;
    demoFixture.forEach((rec, i) => {
      let scheduled = (rec.t * 1000) / speedup;
      if (i > 0 && scheduled - prevScheduled < REPLAY_MIN_GAP_MS) {
        scheduled = prevScheduled + REPLAY_MIN_GAP_MS;
      }
      prevScheduled = scheduled;
      timers.push(setTimeout(() => {
        if (signal?.aborted) return;
        onEvent(rec.event);
        if (i === demoFixture.length - 1) {
          signal?.removeEventListener('abort', onAbort);
          resolve();
        }
      }, scheduled));
    });
  });
}
